import { useCallback, useEffect, useState } from 'react';
import { getApiUrl } from '../config';
import type { Achievement } from '../types/achievement';

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

interface UseUnseenAchievementsResult {
  unseen: Achievement[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  markRevealed: (id: string) => Promise<Achievement | null>;
}

export function useUnseenAchievements(): UseUnseenAchievementsResult {
  const [unseen, setUnseen] = useState<Achievement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(getApiUrl('/me/achievements?onlyUnseen=true'), {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`http ${res.status}`);
      const json = await res.json();
      setUnseen(json.achievements ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'fetch failed');
      setUnseen([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const markRevealed = useCallback(async (id: string): Promise<Achievement | null> => {
    try {
      const res = await fetch(getApiUrl(`/me/achievements/${id}/reveal`), {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) return null;
      const json = await res.json();
      setUnseen(prev => prev.filter(a => a.id !== id));
      return json.achievement ?? null;
    } catch {
      return null;
    }
  }, []);

  return { unseen, loading, error, refresh, markRevealed };
}
